/**
 * OperatingManualDemo — exercises the OperatingManualPage with mock data
 * matching the API response shape from /api/operating-manual.
 *
 * Used as a no-backend-required design preview while:
 *   1. The Supabase project + migration are being run for the first time
 *   2. The first real Profile is being created via the magic-invite flow
 *
 * Mock data hits every locked structural commitment: Section 5 Tension
 * Alignment guardrail rendered, Section 6 Goal Alignment with worked example,
 * Section 12 manager-fit (manager viewer mode), hedge phrases highlighted in
 * the prose, audit footer with the mono-register stamp.
 */

import OperatingManualPage from "./OperatingManualPage.jsx";

const MOCK_RESPONSE = {
  blueprint_id: "OM-DEMO-2026-0502-00001",
  title: "Operating Manual: Sarah",
  subject_first_name: "Sarah",
  viewer_mode: "manager",
  subject_mode: "Full",
  generated_at_iso: "2026-05-02T14:30:00Z",
  engine_version: "elemental-influence@1.0.0+2026-05-02",
  generation_latency_ms: 18420,

  hedge_phrases: [
    "tends to",
    "probably",
    "likely",
    "may",
    "watch for",
    "expect",
  ],

  tension_alignment_guardrail_rendered: true,
  goal_alignment_worked_example_included: true,
  section_12_rendered: true,

  operating_manual_markdown: [
    "## Section 1 — Snapshot",
    "",
    "Sarah drives toward outcomes; she gets restless in long deliberations and brings urgency that lifts the room when channeled and pressures it when not. In a typical week she ships fast, names problems before others see them, and tends to collide with peers who need more processing time. The tension worth naming up front is impatience with consensus-building.",
    "",
    "## Section 2 — Strengths",
    "",
    "- Decisive under ambiguity. When the team is stuck on which path to take, Sarah tends to be the one who commits and keeps the meeting moving.",
    "- Honest in her read of work. She'll name a draft as 'not there yet' rather than soften it, which is rare and worth keeping.",
    "- Strong sense of structural fairness. She'll watch for whose voice isn't getting room, even when it slows her down.",
    "- Pattern-spotter across systems. She tends to surface the connection between two seemingly unrelated bugs faster than peers.",
    "",
    "## Section 3 — Weaknesses / Blind Spots",
    "",
    "- May read deliberation as avoidance and push past it before peers feel heard. Watch for signs she's already decided before the meeting started.",
    "- Probably underestimates how much her speed costs collaborators who need more processing time.",
    "- Tends to over-trust her first read on a person and may miss when someone is bracing rather than disagreeing.",
    "",
    "## Section 4 — Internal Drivers",
    "",
    "Mastery over hard problems is the primary engine. Sarah is probably energized by being the person who got the work right when no one else thought it was worth doing properly. Watch for: public recognition probably doesn't move her the way it does most people on the platform team — she tends to find it embarrassing rather than energizing. Quiet, specific, technical respect from peers she trusts lands hardest.",
    "",
    "What does NOT motivate her: titles for their own sake, generic 'great job!' praise, perks-as-recognition.",
    "",
    "## Section 5 — Tension Alignment",
    "",
    "> [!Watch] Tension Alignment is a tool for channeling avoidance energy into shared purpose, not a list of buttons to push. Misuse is grounds for HR-tier admin review.",
    "",
    "Sarah probably stands hardest against being dismissed as 'just engineering' in cross-functional rooms. She tends to work hardest when she has something to prove to a peer who has discounted her work. Watch for: chaos and unclear ownership are her highest stress signals — when ownership goes ambiguous, she often takes more on rather than risk a seam.",
    "",
    "The historical pattern that shaped this: an early role where she shipped good work and saw it credited elsewhere. The threat radar tends to fire on that pattern even when the current room isn't doing it.",
    "",
    "## Section 6 — Goal Alignment Map",
    "",
    "How to frame organizational goals so they hit both her approach drivers (Section 4) and avoidance drivers (Section 5). The strongest framings hit both engines at once.",
    "",
    "Worked example. Same Q3 launch goal — your version: *\"ship on time.\"* Her version: *\"be the person who got this right when no one else thought it was worth doing properly **and** don't let the team get steamrolled by the rushed timeline the exec floor wants.\"* Same outcome — but the framing pulls toward craftsmanship (approach) AND pushes away from being steamrolled (avoidance). Both engines firing.",
    "",
    "## Section 7 — Recognition Style",
    "",
    "Quiet and specific. Public recognition probably feels patronizing; private acknowledgment of a hard call she made well lands.",
    "",
    "- **Lands:** Slack DM with a specific reference. \"The way you caught the race condition before staging — that was you, not luck.\"",
    "- **Doesn't land:** all-hands shoutouts, generic team-channel praise, \"team player\" framing.",
    "",
    "## Section 8 — Communication Preferences",
    "",
    "- **Channel:** Slack primary, email secondary, in-person reserved for hard conversations only.",
    "- **Cadence:** weekly 1:1; on-demand for blockers.",
    "- **Length:** one-liner default; full doc when stakes are high.",
    "- **Formality:** casual prose; structured for decision docs.",
    "- **Response-time expectations:** same-day for Slack, next-day for email.",
    "",
    "## Section 9 — Conflict Style Under Pressure",
    "",
    "Sarah tends to confront directly when she has data, withdraws when she doesn't yet have her read. Watch for the silence — it's not avoidance, it's calibration. Fill it and you'll get the rehearsed answer instead of the real one.",
    "",
    "## Section 10 — Career Arc Tendencies",
    "",
    "Probably restless on a 2-3 year cycle. Likely thrives in a role that combines technical depth with cross-functional translation; tends to plateau in pure-IC depth roles after about 18 months. Stretch assignment that would grow her: leading a project that requires bringing along a peer who initially disagrees with the technical direction.",
    "",
    "## Section 11 — Recent Context",
    "",
    "- *2026-04-28:* Logged 1:1 with Marcus. Outcome: agreed on Q3 scope after some friction on timeline.",
    "- *2026-04-22:* Recent tag: *\"caught race condition pre-staging\"* (decision_making, attributed to Marcus).",
    "- *2026-04-15:* Career event: lateral move to Platform team.",
    "- *No pinned snapshots in the last 90 days.*",
    "",
    "## Section 12 — Manager-Fit",
    "",
    "Where you and Sarah probably align: both of you tend to commit fast and value craft. The shared rhythm is a big asset.",
    "",
    "Where it tends to rub: when you're in deliberation mode and she's already decided. She'll read your processing as you holding back; you'll read her speed as her not listening. Both of you are probably right about something.",
    "",
    "The compounding move: name the deliberation explicitly. \"I haven't decided yet, give me a beat\" tends to disarm her — it tells her your silence isn't strategic withholding. She extends the same courtesy back when she sees the pattern.",
  ].join("\n"),
};

export default function OperatingManualDemo() {
  return (
    <div
      style={{
        background: "var(--ppr-color-background-sub)",
        padding: "32px 0",
        minHeight: "100vh",
      }}
    >
      <OperatingManualPage
        data={MOCK_RESPONSE}
        consentRecord="sarah.chen@ · 2026-04-12"
      />
    </div>
  );
}
